import { CompanyResultRow, CompanyResultsResponse, ResultsPeriodOption, ResultsSelection } from './useCompanyResults';

/**
 * Logique d'affichage des résultats des sociétés, partagée par la table desktop et la liste
 * mobile. Aucune donnée n'est recalculée ici : les écarts (valeur et %) viennent de l'API ;
 * on ne fait que formater, filtrer, trier, regrouper et compter.
 */

export type Metric = 'revenue' | 'ebit' | 'net_income';
export type SortKey = 'symbole' | Metric | `${Metric}_var`;
export type PeriodKey = 'target_period' | 'base_period';

export interface MetricDef {
  key: Metric;
  label: string;
  short: string;
  title: string;
}

export const METRICS: MetricDef[] = [
  { key: 'revenue', label: 'CA / PNB', short: 'CA / PNB', title: "Chiffre d'affaires ; Produit Net Bancaire pour les banques" },
  { key: 'ebit', label: "Résultat d'exploitation", short: 'Rés. exploit.', title: 'EBIT' },
  { key: 'net_income', label: 'Résultat net', short: 'Résultat net', title: 'Résultat net publié (consolidé pour les groupes)' },
];

export const POSITIVE = '#00b638';
export const NEGATIVE = '#ef5350';

export const PERIOD_LABELS: Record<string, string> = {
  Q1: 'T1 (Trimestre 1)',
  S1: 'S1 (Semestre 1)',
  '9M': '9M (Neuf mois)',
  ANNUAL: 'Annuel',
};

export const periodShort = (periodType: string) =>
  (periodType === 'ANNUAL' ? 'Annuel' : periodType === 'Q1' ? 'T1' : periodType);

export const BASE_NON_POSITIVE_HINT = 'Base négative ou nulle : variation exprimée en valeur (M FCFA), le % serait trompeur.';

/** Montant en millions de FCFA, à la manière des bulletins de place (« MXOF »). */
export const toMillions = (value: number): string => {
  const m = value / 1_000_000;
  return m.toLocaleString('fr-FR', { maximumFractionDigits: Math.abs(m) < 10 ? 1 : 0 });
};

export const signed = (text: string, value: number) => (value > 0 ? `+${text}` : text);

export const formatPct = (pct: number) =>
  `${signed(pct.toLocaleString('fr-FR', { maximumFractionDigits: 1, minimumFractionDigits: 1 }), pct)} %`;

/** Macro-secteur : « Services Financiers/Banque » → « Services Financiers ». */
export const sectorGroup = (secteur: string | null) => (secteur ? secteur.split('/')[0].trim() : 'Autres');

export const hasPublished = (row: CompanyResultRow) =>
  row.target_period.revenue != null || row.target_period.ebit != null || row.target_period.net_income != null;

export const metricValue = (row: CompanyResultRow, metric: Metric, period: PeriodKey) =>
  row[period]?.[metric] ?? null;

/** Variation d'une ligne telle que fournie par l'API : en % si la base est positive, sinon en valeur. */
export type Variation =
  | { kind: 'pct'; value: number }
  | { kind: 'abs'; value: number }
  | { kind: 'none' };

export const rowVariation = (row: CompanyResultRow, metric: Metric): Variation => {
  const pct = row[`${metric}_var_pct`];
  if (pct != null) return { kind: 'pct', value: pct };
  const abs = row[`${metric}_var_abs`];
  if (abs != null) return { kind: 'abs', value: abs };
  return { kind: 'none' };
};

/** Variation agrégée d'un groupe : sociétés dont N et N-1 sont comparables (écart calculé par l'API). */
export const aggregateVariation = (rows: CompanyResultRow[], metric: Metric): number | null => {
  let sumAbs = 0;
  let sumBase = 0;
  let n = 0;
  rows.forEach(r => {
    const abs = r[`${metric}_var_abs`];
    const base = metricValue(r, metric, 'base_period');
    if (abs == null || base == null) return;
    sumAbs += abs;
    sumBase += base;
    n += 1;
  });
  if (n === 0 || sumBase <= 0) return null;
  return (sumAbs / sumBase) * 100;
};

/**
 * Diffusion des hausses : parmi les sociétés comparables (écart fourni par l'API), combien
 * progressent. Simple dénombrement, volontairement sans agrégat monétaire : sommer PNB et
 * CA de secteurs différents donnerait un chiffre dominé par deux ou trois capitalisations.
 */
export const breadth = (rows: CompanyResultRow[], metric: Metric): { up: number; comparable: number } => {
  let up = 0;
  let comparable = 0;
  rows.forEach(r => {
    const abs = r[`${metric}_var_abs`];
    if (abs == null) return;
    comparable += 1;
    if (abs > 0) up += 1;
  });
  return { up, comparable };
};

export interface ResultsGroup {
  name: string | null;
  rows: CompanyResultRow[];
}

export interface ResultsViewOptions {
  searchTerm: string;
  publishedOnly: boolean;
  groupBySector: boolean;
  sort: { key: SortKey; direction: 'asc' | 'desc' };
}

export const filterAndSort = (rows: CompanyResultRow[], opts: ResultsViewOptions): CompanyResultRow[] => {
  const { searchTerm, publishedOnly, sort } = opts;
  const term = searchTerm.toLowerCase();
  const sortValue = (r: CompanyResultRow): string | number | null => {
    if (sort.key === 'symbole') return r.symbole;
    if (sort.key.endsWith('_var')) {
      const metric = sort.key.replace('_var', '') as Metric;
      return r[`${metric}_var_pct`] ?? null;
    }
    return metricValue(r, sort.key as Metric, 'target_period');
  };
  return rows
    .filter(r => (!publishedOnly || hasPublished(r)) && (
      r.symbole.toLowerCase().includes(term) || (r.nom_societe ?? '').toLowerCase().includes(term)
    ))
    .sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      // Donnée absente : toujours en fin de liste, quel que soit le sens du tri.
      if (av == null || bv == null) return av == null ? (bv == null ? 0 : 1) : -1;
      const cmp = typeof av === 'string' ? av.localeCompare(String(bv)) : av - (bv as number);
      return sort.direction === 'asc' ? cmp : -cmp;
    });
};

export const groupRows = (rows: CompanyResultRow[], groupBySector: boolean): ResultsGroup[] => {
  if (!groupBySector) return [{ name: null, rows }];
  const map = new Map<string, CompanyResultRow[]>();
  rows.forEach(r => {
    const g = sectorGroup(r.secteur);
    map.set(g, [...(map.get(g) ?? []), r]);
  });
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, groupRows]) => ({ name, rows: groupRows }));
};

/** Contrat commun des vues desktop et mobile : le conteneur décide, les vues affichent. */
export interface ResultsPageProps {
  periodOptions: string[];
  yearOptions: ResultsPeriodOption[];
  selection: ResultsSelection | null;
  onPeriodChange: (periodType: string) => void;
  onExerciceChange: (exercice: number) => void;
  selectedOption: ResultsPeriodOption | null;
  /** Couverture sous le seuil serveur : les chiffres affichés sont provisoires. */
  partialCoverage: boolean;
  results: CompanyResultsResponse | null;
  loading: boolean;
  error: string | null;
}
